import { useCallback } from "react";

import { cn } from "@btnet/utilities-core";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTInput, type IBTInputProps } from "commonComponents/btWrappers/BTInput/BTInput";
import {
    type IInputDebouncingContext,
    useDebouncedOnChange,
} from "commonComponents/utilities/Input/InputDebouncingContext";

interface IAutoSearchInputProps<T>
    extends Omit<IBTInputProps<T>, "onChange" | "debouncingContext"> {
    /** Executed instantly and on every change */
    onChange: (field: KeyOfOrString<T>, value: string) => void;
    /** Only executed if nothing changes for a set amount of time */
    onChangeDebounced: (field: KeyOfOrString<T>, value: string) => void;
    className?: string;

    /** Default delay is 500ms */
    inputDelay?: number;
    maxDelay?: number;
}

export const AutoSearchInput = track({
    component: "AutoSearch Input",
})(function <T = undefined>({
    value,
    className,
    onChange,
    onChangeDebounced,
    inputDelay = 500,
    maxDelay,
    ...otherProps
}: IAutoSearchInputProps<T> & ITrackingProp) {
    const [debouncedValue, debouncedOnChange] = useDebouncedOnChange<typeof value, T>(
        value ?? "",
        onChangeDebounced,
        { useDebouncing: true, inputDelay, maxDelay } as IInputDebouncingContext,
    );

    const semiDebouncedOnChange = useCallback(
        (field: KeyOfOrString<T>, value: string) => {
            onChange(field, value);
            debouncedOnChange(field, value);
        },
        [onChange, debouncedOnChange],
    );

    return (
        <BTInput
            {...otherProps}
            value={debouncedValue}
            className={cn("AutoSearchInput", className)}
            onChange={semiDebouncedOnChange}
            debouncingContext={{ useDebouncing: false }}
            hotkey="search"
        />
    );
});
